import axios from "const/axios";
import { API_URL_PUBLIC } from "const/constants";
import publicAxios from "axios";

const STORAGE_KEY = "checkInFormConfig";

const DEFAULT_FIELDS = [
  { id: "name", label: "Full Name", type: "text", required: true, enabled: true, layout: "half" },
  { id: "number", label: "Phone", type: "tel", required: true, enabled: true, layout: "half" },
  { id: "email", label: "Email", type: "email", required: true, enabled: true, layout: "half" },
  { id: "address", label: "Address", type: "text", required: true, enabled: true, layout: "half" },
  {
    id: "emergencyContactName",
    label: "Emergency Contact Name",
    type: "text",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "emergencyContact",
    label: "Emergency Contact Phone",
    type: "tel",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "visaStatus",
    label: "Visa Type",
    type: "text",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "visaExpiry",
    label: "Visa Expiry Date",
    type: "date",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "nationality",
    label: "Country of Passport",
    type: "text",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "gender",
    label: "Gender",
    type: "select",
    required: false,
    enabled: true,
    layout: "half",
    options: ["Male", "Female", "Other"],
  },
  {
    id: "dob",
    label: "Date of Birth",
    type: "date",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "maritalStatus",
    label: "Marital Status",
    type: "select",
    required: false,
    enabled: true,
    layout: "half",
    options: ["Single", "Married", "Divorced", "Other"],
  },
  {
    id: "usiNumber",
    label: "USI Number",
    type: "text",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "passportNumber",
    label: "Passport Number",
    type: "text",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "dateOfIssue",
    label: "Passport Issue Date",
    type: "date",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "passportExpiry",
    label: "Passport Expiry Date",
    type: "date",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "emergencyRelation",
    label: "Emergency Contact Relation",
    type: "text",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "emergencyEmail",
    label: "Emergency Contact Email",
    type: "email",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "interestedServices",
    label: "Interested Services",
    type: "checkbox-group",
    required: false,
    enabled: true,
    layout: "full",
    options: [
      "Admission",
      "Visa",
      "Consultation",
      "RPL Certification",
      "Skills Assessment",
      "Insurance",
      "Other",
    ],
  },
  {
    id: "source",
    label: "How did you hear about us?",
    type: "autocomplete",
    required: false,
    enabled: true,
    layout: "full",
    options: ["Google", "Facebook", "Tiktok", "Instagram", "Family or Friends.", "Other"],
  },
  {
    id: "comments",
    label: "Comments",
    type: "textarea",
    required: false,
    enabled: true,
    layout: "full",
  },
];

const normalizeConfig = (storedConfig) => {
  const defaultById = new Map(DEFAULT_FIELDS.map((field) => [field.id, field]));
  const used = new Set();
  const normalized = [];
  const customFields = [];

  if (Array.isArray(storedConfig)) {
    storedConfig.forEach((item) => {
      if (!item || !item.id) return;

      // Check if it's a default field
      if (defaultById.has(item.id)) {
        const base = defaultById.get(item.id);
        normalized.push({ ...base, ...item });
        used.add(item.id);
      } else if (item.isCustom) {
        // It's a custom field
        customFields.push(item);
      }
    });
  }

  // Add default fields that weren't in stored config
  DEFAULT_FIELDS.forEach((field) => {
    if (!used.has(field.id)) normalized.push({ ...field });
  });

  // Append custom fields at the end
  return [...normalized, ...customFields];
};

export const getDefaultCheckInFormConfig = () => DEFAULT_FIELDS.map((f) => ({ ...f }));

// Used by admin settings - reads from API, falls back to localStorage then defaults
export const getCheckInFormConfig = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultCheckInFormConfig();
    const parsed = JSON.parse(raw);
    return normalizeConfig(parsed);
  } catch {
    return getDefaultCheckInFormConfig();
  }
};

export const setCheckInFormConfig = (config) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
};

// API-backed functions for admin settings
export const fetchCheckInFormConfigFromAPI = async () => {
  try {
    const res = await axios.get("/check-in-form/config");
    const fieldsJson = res?.data?.data?.fieldsJson;
    if (fieldsJson) {
      const parsed = JSON.parse(fieldsJson);
      // Keep localStorage in sync as cache
      localStorage.setItem(STORAGE_KEY, fieldsJson);
      return normalizeConfig(parsed);
    }
    return getDefaultCheckInFormConfig();
  } catch {
    return getCheckInFormConfig();
  }
};

export const saveCheckInFormConfigToAPI = async (config) => {
  const fieldsJson = JSON.stringify(config);
  await axios.put("/check-in-form/config", { fieldsJson });
  localStorage.setItem(STORAGE_KEY, fieldsJson);
};

// Used by public form - fetches from public endpoint by slug
export const fetchCheckInFormConfigBySlug = async (slug) => {
  try {
    const res = await publicAxios.get(`${API_URL_PUBLIC}/public/client-form/${slug}`, {
      headers: { Authorization: null },
    });
    const fieldsJson = res?.data?.data?.fieldsJson;
    if (fieldsJson) {
      return { fields: normalizeConfig(JSON.parse(fieldsJson)), orgInfo: res.data.data };
    }
    return { fields: getDefaultCheckInFormConfig(), orgInfo: res?.data?.data };
  } catch {
    return { fields: getDefaultCheckInFormConfig(), orgInfo: null };
  }
};
